import React from 'react';

/** Renders Echo Index blueprint markdown: # h1, **bold**, `-` bullets. */
function inlineBold(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const m = remaining.match(/\*\*(.+?)\*\*/);
    if (m && m.index !== undefined) {
      if (m.index > 0) {
        parts.push(<span key={key++}>{remaining.slice(0, m.index)}</span>);
      }
      parts.push(
        <strong key={key++} className="font-semibold text-white">
          {m[1]}
        </strong>
      );
      remaining = remaining.slice(m.index + m[0].length);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return <>{parts}</>;
}

export function BlueprintMarkdown({ markdown }: { markdown: string }) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let paraLines: string[] = [];
  const listItems: string[] = [];
  const orderedItems: string[] = [];
  let inCode = false;
  let codeLines: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`b-${blocks.length}`} className="mb-4 list-none space-y-1.5">
        {listItems.splice(0, listItems.length).map((item, i) => (
          <li
            key={i}
            className="text-sm leading-relaxed text-white/80 pl-2 border-l-2 border-gold-button/40"
          >
            {inlineBold(item.trim())}
          </li>
        ))}
      </ul>
    );
  };
  const flushOrdered = () => {
    if (orderedItems.length === 0) return;
    blocks.push(
      <ol key={`b-${blocks.length}`} className="mb-4 list-decimal list-inside space-y-1.5">
        {orderedItems.splice(0, orderedItems.length).map((item, i) => (
          <li key={i} className="text-sm leading-relaxed text-white/80">
            {inlineBold(item.trim())}
          </li>
        ))}
      </ol>
    );
  };
  const flushCode = () => {
    if (codeLines.length === 0) return;
    blocks.push(
      <pre
        key={`b-${blocks.length}`}
        className="mb-4 overflow-x-auto rounded border border-border-light bg-black p-3 text-xs leading-relaxed text-white/80"
      >
        <code>{codeLines.join('\n')}</code>
      </pre>
    );
    codeLines = [];
  };

  const flushPara = () => {
    if (paraLines.length === 0) return;
    const chunk = paraLines.splice(0, paraLines.length);
    blocks.push(
      <div key={`b-${blocks.length}`} className="mb-3 text-sm leading-relaxed text-white/80">
        {chunk.map((pl, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 ? <br /> : null}
            {inlineBold(pl.trim())}
          </React.Fragment>
        ))}
      </div>
    );
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const trimmed = line.trim();
    if (trimmed === '') {
      if (inCode) {
        codeLines.push('');
        continue;
      }
      flushList();
      flushOrdered();
      flushPara();
      continue;
    }
    if (trimmed.startsWith('```')) {
      if (!inCode) {
        flushList();
        flushOrdered();
        flushPara();
        inCode = true;
        continue;
      }
      inCode = false;
      flushCode();
      continue;
    }
    if (inCode) {
      codeLines.push(line);
      continue;
    }
    if (trimmed === '---') {
      flushList();
      flushOrdered();
      flushPara();
      blocks.push(<hr key={`b-${blocks.length}`} className="my-5 border-border-light" />);
      continue;
    }
    const hMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (hMatch) {
      flushList();
      flushOrdered();
      flushPara();
      const level = hMatch[1].length;
      const text = hMatch[2];
      if (level === 1) {
        blocks.push(
          <h1
            key={`b-${blocks.length}`}
            className="text-2xl font-serif font-bold text-white mb-4 mt-2 first:mt-0"
          >
            {inlineBold(text)}
          </h1>
        );
      } else {
        blocks.push(
          <h2
            key={`b-${blocks.length}`}
            className="text-xl font-serif font-semibold text-white mt-6 mb-3"
          >
            {inlineBold(text)}
          </h2>
        );
      }
      continue;
    }
    if (trimmed.startsWith('- ')) {
      flushPara();
      flushOrdered();
      listItems.push(trimmed.slice(2).trim());
      continue;
    }
    const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ordered) {
      flushPara();
      flushList();
      orderedItems.push(ordered[1].trim());
      continue;
    }
    flushList();
    flushOrdered();
    paraLines.push(trimmed);
  }
  flushList();
  flushOrdered();
  flushPara();
  flushCode();

  return <div className="blueprint-markdown max-w-none text-white/80">{blocks}</div>;
}
